<template>
  <div class="source-performance-report">
    <div class="bg-white">
      <h1 class="text-2xl font-bold text-gray-900 mb-6">
        Data Kaynağı Performans Paneli
      </h1>

      <!-- Filtreler -->
      <div class="filters-container mb-6">
        <div class="flex flex-wrap gap-4 items-end">
          <div class="flex-1 min-w-[160px]">
            <label class="block text-sm font-medium text-gray-700 mb-2">Başlangıç Tarihi</label>
            <input
              v-model="startDate"
              type="date"
              class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div class="flex-1 min-w-[160px]">
            <label class="block text-sm font-medium text-gray-700 mb-2">Bitiş Tarihi</label>
            <input
              v-model="endDate"
              type="date"
              class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div class="flex-1 min-w-[180px]">
            <label class="block text-sm font-medium text-gray-700 mb-2">Data Kaynağı</label>
            <select
              v-model="selectedSource"
              class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Hepsi</option>
              <option v-for="s in sourceOptions" :key="s" :value="s">{{ s }}</option>
            </select>
          </div>
          <div class="flex-1 min-w-[180px]">
            <label class="block text-sm font-medium text-gray-700 mb-2">Satış Temsilcisi</label>
            <select
              v-model="selectedAgent"
              class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Hepsi</option>
              <option v-for="a in agentOptions" :key="a.value" :value="a.value">{{ a.label }}</option>
            </select>
          </div>
          <div>
            <button
              @click="loadReport"
              :disabled="loading"
              class="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {{ loading ? 'Yükleniyor...' : 'Filtrele' }}
            </button>
          </div>
        </div>
      </div>

      <!-- Hata -->
      <div v-if="errorMessage" class="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-md">
        {{ errorMessage }}
      </div>

      <!-- Eşlenmemiş statüler uyarısı -->
      <div
        v-if="unmapped.length"
        class="mb-4 p-3 bg-yellow-50 border border-yellow-200 text-yellow-800 rounded-md text-sm"
      >
        <strong>Uyarı:</strong> Aşağıdaki statüler hiçbir huni kategorisine eşlenmedi (Toplam Data'ya dahil ama yüzde kırılımlarında yok):
        <span class="font-medium">{{ unmapped.join(', ') }}</span>
      </div>

      <!-- Tablo -->
      <div v-if="rows.length" class="overflow-x-auto border border-gray-200 rounded-lg">
        <table class="min-w-full bg-white text-sm">
          <thead class="bg-gray-50">
            <tr>
              <th
                v-for="col in columns"
                :key="col.field"
                @click="setSort(col.field)"
                class="px-4 py-3 font-semibold text-gray-600 border-b cursor-pointer select-none hover:bg-gray-100"
                :class="col.field === 'source' ? 'text-left' : 'text-right'"
              >
                {{ col.label }}
                <span class="text-blue-500">{{ sortArrow(col.field) }}</span>
              </th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-200">
            <tr v-for="row in sortedRows" :key="row.source" class="hover:bg-gray-50">
              <td class="px-4 py-3 text-gray-900 font-medium">{{ row.source }}</td>
              <td class="px-4 py-3 text-right text-gray-900">{{ formatInt(row.total_data) }}</td>
              <td class="px-4 py-3 text-right" :class="cleanClass(row.clean_pct)">{{ formatPct(row.clean_pct) }}</td>
              <td class="px-4 py-3 text-right text-gray-700">{{ formatPct(row.contact_pct) }}</td>
              <td class="px-4 py-3 text-right text-green-700">{{ formatPct(row.potential_pct) }}</td>
              <td class="px-4 py-3 text-right text-gray-700">{{ formatPct(row.operational_pct) }}</td>
              <td class="px-4 py-3 text-right text-red-600">{{ formatPct(row.negative_pct) }}</td>
              <td class="px-4 py-3 text-right text-gray-900 font-semibold">{{ formatInt(row.customers) }}</td>
              <td class="px-4 py-3 text-right font-bold" :class="crClass(row.net_cr)">{{ formatPct(row.net_cr) }}</td>
            </tr>
          </tbody>
          <tfoot v-if="totals" class="bg-gray-100 font-semibold">
            <tr>
              <td class="px-4 py-3 text-gray-900">{{ totals.source }}</td>
              <td class="px-4 py-3 text-right">{{ formatInt(totals.total_data) }}</td>
              <td class="px-4 py-3 text-right">{{ formatPct(totals.clean_pct) }}</td>
              <td class="px-4 py-3 text-right">{{ formatPct(totals.contact_pct) }}</td>
              <td class="px-4 py-3 text-right">{{ formatPct(totals.potential_pct) }}</td>
              <td class="px-4 py-3 text-right">{{ formatPct(totals.operational_pct) }}</td>
              <td class="px-4 py-3 text-right">{{ formatPct(totals.negative_pct) }}</td>
              <td class="px-4 py-3 text-right">{{ formatInt(totals.customers) }}</td>
              <td class="px-4 py-3 text-right">{{ formatPct(totals.net_cr) }}</td>
            </tr>
          </tfoot>
        </table>
      </div>

      <div v-else-if="hasSearched && !loading" class="text-gray-500 py-8 text-center">
        Seçilen kriterlere uygun kayıt bulunamadı.
      </div>
    </div>
  </div>
</template>

<script>
import http from '../services/http'

export default {
  name: 'SourcePerformanceReport',
  data() {
    const today = new Date()
    const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1)
    return {
      startDate: firstDayOfMonth.toISOString().split('T')[0],
      endDate: today.toISOString().split('T')[0],
      selectedSource: '',
      selectedAgent: '',
      rows: [],
      totals: null,
      unmapped: [],
      sourceOptions: [],
      loading: false,
      hasSearched: false,
      errorMessage: '',
      sortField: 'total_data',
      sortDir: 'desc',
      columns: [
        { field: 'source', label: 'Data Kaynağı' },
        { field: 'total_data', label: 'Toplam Data' },
        { field: 'clean_pct', label: 'Temiz Data %' },
        { field: 'contact_pct', label: 'Temas Oranı %' },
        { field: 'potential_pct', label: 'Potansiyel %' },
        { field: 'operational_pct', label: 'Operasyonel Takip %' },
        { field: 'negative_pct', label: 'Olumsuzluk %' },
        { field: 'customers', label: 'Müşteri' },
        { field: 'net_cr', label: 'Net CR %' }
      ]
    }
  },
  computed: {
    agentOptions() {
      const opts = (this.$root.cache && this.$root.cache.getOptions && this.$root.cache.getOptions[15]) || []
      return opts
    },
    sortedRows() {
      const dir = this.sortDir === 'asc' ? 1 : -1
      const field = this.sortField
      return [...this.rows].sort((a, b) => {
        let x = a[field]
        let y = b[field]
        if (typeof x === 'string' || typeof y === 'string') {
          return String(x).localeCompare(String(y), 'tr') * dir
        }
        return ((x || 0) - (y || 0)) * dir
      })
    }
  },
  mounted() {
    this.loadReport()
  },
  methods: {
    async loadReport() {
      this.loading = true
      this.errorMessage = ''
      try {
        const response = await http.getSourcePerformanceReport({
          start_date: this.startDate,
          end_date: this.endDate,
          source: this.selectedSource || null,
          agent_id: this.selectedAgent || null
        })
        const res = response.data
        if (!res || res.success === false) {
          this.errorMessage = (res && res.message) || 'Rapor yüklenemedi'
          this.rows = []
          this.totals = null
          this.unmapped = []
        } else {
          this.rows = res.data || []
          this.totals = res.totals || null
          this.unmapped = res.unmapped || []
          // Kaynak filtresi tümü ise seçenek listesini güncelle
          if (!this.selectedSource) {
            this.sourceOptions = this.rows.map(r => r.source)
          }
        }
      } catch (e) {
        this.errorMessage = 'Rapor yüklenirken bir hata oluştu'
      } finally {
        this.loading = false
        this.hasSearched = true
      }
    },
    setSort(field) {
      if (this.sortField === field) {
        this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc'
      } else {
        this.sortField = field
        this.sortDir = field === 'source' ? 'asc' : 'desc'
      }
    },
    sortArrow(field) {
      if (this.sortField !== field) return ''
      return this.sortDir === 'asc' ? ' ▲' : ' ▼'
    },
    formatInt(n) {
      return Number(n || 0).toLocaleString('tr-TR')
    },
    formatPct(n) {
      return '%' + Number(n || 0).toFixed(2)
    },
    cleanClass(v) {
      if (v >= 85) return 'text-green-700 font-semibold'
      if (v >= 60) return 'text-yellow-600'
      return 'text-red-600'
    },
    crClass(v) {
      if (v >= 5) return 'text-green-700'
      if (v >= 2) return 'text-yellow-600'
      return 'text-red-600'
    }
  }
}
</script>

<style scoped>
.source-performance-report {
  @apply max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8;
}

.filters-container {
  @apply bg-gray-50 rounded-lg p-4;
}
</style>
